import {
  Step,
  StepperProvider,
  useStepper,
} from '@/components/Molecules/Stepper'
import { ApplicationWizardStep } from '@/@types/generated'
import { buildPath, Params, RouteKey } from '@/routes'
import { ComponentProps, PropsWithChildren, useMemo } from 'react'
import { Outlet, useParams } from 'react-router'
import { ApplicationStepGuard } from '@/modules/applications/components/ApplicationStepGuard'
import { CertificateApplicationProgress } from '../components/CertificateApplicationProgress'
import {
  CertificateApplicationProvider,
  useCertificateApplication,
} from './application-provider'
import { CertificateApplicationStepGuard } from '../routes/CertificateApplicationStepGuard'

export const MotorCarrierSteps = [
  {
    title: 'Company Information',
    key: ApplicationWizardStep.COMPANY_INFORMATION,
    path: '/applications/certificate/motor-carrier',
  },
  {
    title: 'Review SAFER',
    key: ApplicationWizardStep.REVIEW_SAFER,
    path: '/applications/certificate/:applicationId/motor-carrier/review-safer',
  },
  {
    title: 'Business Structure',
    key: ApplicationWizardStep.BUSINESS_STRUCTURE,
    path: '/applications/certificate/:applicationId/motor-carrier/business-information',
  },
  {
    title: 'Texas Agent',
    key: ApplicationWizardStep.TEXAS_AGENT,
    path: '/applications/certificate/:applicationId/motor-carrier/texas-agent',
  },
  {
    title: 'Interstate UCR',
    key: ApplicationWizardStep.INTERSTATE_UCR,
    path: '/applications/certificate/:applicationId/motor-carrier/interstate-ucr',
  },
  {
    title: 'Registration Period',
    key: ApplicationWizardStep.REGISTRATION_PERIOD,
    path: '/applications/certificate/:applicationId/motor-carrier/registration-period',
  },
  {
    title: 'Cargo Types',
    key: ApplicationWizardStep.CARGO_TYPES,
    path: '/applications/certificate/:applicationId/motor-carrier/cargo-types',
  },
  {
    title: 'Vehicles',
    key: ApplicationWizardStep.VEHICLES,
    path: '/applications/certificate/:applicationId/motor-carrier/vehicles',
  },
  {
    title: 'Drug Test Certification',
    key: ApplicationWizardStep.DRUG_TEST_CERTIFICATION,
    path: '/applications/certificate/:applicationId/motor-carrier/drug-test-certification',
  },
  {
    title: 'Questionnaire',
    key: ApplicationWizardStep.QUESTIONNAIRE,
    path: '/applications/certificate/:applicationId/motor-carrier/questionnaire',
  },
  {
    title: 'Review & Submit',
    key: ApplicationWizardStep.REVIEW_AND_SUBMIT,
    path: '/applications/certificate/:applicationId/motor-carrier/review',
  },
] as const satisfies (Step & { path: RouteKey; key: ApplicationWizardStep })[]

export function MotorCarrierStepsProvider({ children }: PropsWithChildren) {
  const { applicationId } =
    useParams<
      Params<'/applications/certificate/:applicationId/motor-carrier'>
    >()

  // Memoize once
  const orderedSteps = useMemo(
    () => resolveSteps(applicationId!),
    [applicationId] // Memoize once
  )

  const submittedPath = buildPath(
    '/applications/certificate/:applicationId/motor-carrier/submitted',
    { applicationId: applicationId! }
  )

  return (
    <StepperProvider steps={orderedSteps}>
      <CertificateApplicationProvider applicationId={applicationId!}>
        <CertificateApplicationStepGuard>
          <div className="md:mx-none mx-4 flex flex-col gap-4">
            <CertificateApplicationProgress allComplete={submittedPath} />
            <LoadedApplicationStepGuard
              applicationId={applicationId!}
              steps={orderedSteps}
              afterLastStepPath={submittedPath}
            >
              {children || <Outlet />}
            </LoadedApplicationStepGuard>
          </div>
        </CertificateApplicationStepGuard>
      </CertificateApplicationProvider>
    </StepperProvider>
  )
}

// The step guard needs the application's status, which is only known inside the provider.
function LoadedApplicationStepGuard(
  props: Omit<ComponentProps<typeof ApplicationStepGuard>, 'status'>
) {
  const { application } = useCertificateApplication()
  return <ApplicationStepGuard {...props} status={application.status} />
}

const resolveSteps = (applicationId: string) =>
  MotorCarrierSteps.map((step) => ({
    ...step,
    path: buildPath(step.path, { applicationId }),
  }))

export function useMotorCarrierSteps(applicationId: string) {
  const orderedSteps = useMemo(
    () => resolveSteps(applicationId),
    [applicationId] // Memoize once
  )

  return useStepper<typeof orderedSteps>()
}
